import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { ShieldCheck, AlertTriangle, TrendingUp, Info, HelpCircle } from 'lucide-react';

/**
 * ScoringSection — Section complète de scoring pour StockDetailPage
 * Affiche Altman Z''-Score, Piotroski F-Score, Beneish M-Score
 * avec graceful degradation si les données ne sont pas encore disponibles
 */
const ScoringSection = ({ stock }) => {
  const [openModal, setOpenModal] = useState(null);

  // Graceful degradation: si tous les scores sont undefined, afficher placeholder
  const hasAnyScoringData = 
    stock?.altman_zscore !== undefined ||
    stock?.piotroski_fscore !== undefined ||
    stock?.beneish_mscore !== undefined ||
    stock?.altman_zone !== undefined ||
    stock?.piotroski_zone !== undefined ||
    stock?.beneish_zone !== undefined ||
    stock?.scoring_exclusion_reason !== undefined;

  if (!hasAnyScoringData) {
    return (
      <Card className="lg:col-span-3 bg-[#1a1a2e] border border-[#2a2a3e] rounded-xl">
        <CardHeader className="border-b border-[#2a2a3e]">
          <CardTitle className="text-white flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#00ff88]" />
            Qualité Comptable & Santé Financière
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <p className="text-gray-400 text-sm text-center py-8">
            Les modèles de scoring apparaîtront ici dès que les données seront disponibles pour ce ticker. 
            Les mises à jour sont traitées chaque semaine.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Check if Vietnam (no_data_vn)
  const isVietnam = stock?.scoring_exclusion_reason === 'no_data_vn';

  // Check if financial sector
  const isFinancial = 
    stock?.altman_zone === 'not_applicable_financials' ||
    stock?.piotroski_zone === 'not_applicable_financials' ||
    stock?.beneish_zone === 'not_applicable_financials' ||
    stock?.scoring_exclusion_reason === 'financial_sector';

  // Format scores
  const formatAltman = (score) => {
    if (score === undefined || score === null) return '—';
    return score.toFixed(2);
  };

  const formatPiotroski = (score) => {
    if (score === undefined || score === null) return '—';
    return Math.round(score).toString();
  };

  const formatBeneish = (score) => {
    if (score === undefined || score === null) return '—';
    if (score < 0) {
      return '−' + Math.abs(score).toFixed(2);
    }
    return score.toFixed(2);
  };

  // Get zone colors
  const getAltmanColor = (zone, score) => {
    if (!zone && score !== undefined && score !== null) return 'text-white';
    if (zone === 'safe') return 'text-emerald-400';
    if (zone === 'grey') return 'text-amber-400';
    if (zone === 'distress') return 'text-red-400';
    return 'text-gray-400';
  };

  const getPiotoskiColor = (score) => {
    if (score === undefined || score === null) return 'text-gray-400';
    if (score >= 7) return 'text-emerald-400';
    if (score >= 4) return 'text-amber-400';
    return 'text-red-400';
  };

  const getBeneishColor = (score) => {
    if (score === undefined || score === null) return 'text-gray-400';
    if (score < -2.22) return 'text-emerald-400';
    return 'text-red-400';
  };

  // Get interpretation texts
  const getAltmanInterpretation = () => {
    if (isVietnam) {
      return "L'intégration des données financières du marché vietnamien est en cours. Les modèles de scoring seront disponibles dans une future mise à jour. Les sources actuelles ne fournissent pas une profondeur historique suffisante pour un calcul fiable.";
    }
    if (stock?.altman_zone === 'not_applicable_financials') {
      return "Le Z''-Score d'Altman ne s'applique pas aux institutions financières. Le modèle a été conçu pour les entreprises industrielles et des marchés émergents, pas pour les banques, assureurs ou financières diversifiées.";
    }
    if (stock?.altman_zscore === null || stock?.altman_zscore === undefined) {
      return "Données financières insuffisantes pour calculer ce score. La situation peut évoluer à mesure que de nouvelles données deviennent disponibles.";
    }
    if (stock?.altman_zone === 'safe') {
      return "La santé financière apparaît solide. Faible probabilité de faillite dans les 2 ans selon les données historiques.";
    }
    if (stock?.altman_zone === 'grey') {
      return "La santé financière est incertaine. Le modèle ne classe pas clairement cette entreprise comme saine ou en détresse. Analyse complémentaire recommandée.";
    }
    if (stock?.altman_zone === 'distress') {
      return "Risque de faillite élevé. Historiquement, plus de 70% des entreprises dans cette zone ont connu des difficultés financières dans les 2 ans. Investiguer avant d'investir.";
    }
    return null;
  };

  const getPiotoskiInterpretation = () => {
    if (isVietnam) {
      return "L'intégration des données financières du marché vietnamien est en cours. Les modèles de scoring seront disponibles dans une future mise à jour. Les sources actuelles ne fournissent pas une profondeur historique suffisante pour un calcul fiable.";
    }
    if (stock?.piotroski_zone === 'not_applicable_financials') {
      return "Le F-Score de Piotroski est structurellement biaisé sur les bilans des institutions financières (signaux F5 et F6 sur le levier et la liquidité). Il ne s'applique pas aux banques ou assureurs.";
    }
    if (stock?.piotroski_coverage === 'insufficient' || stock?.piotroski_fscore === null || stock?.piotroski_fscore === undefined) {
      return "Données financières insuffisantes pour calculer ce score. La situation peut évoluer à mesure que de nouvelles données deviennent disponibles.";
    }
    
    const score = stock?.piotroski_fscore;
    const coverage = stock?.piotroski_coverage;
    let interpretation = '';
    
    if (score >= 7) {
      interpretation = `Qualité comptable solide. L'entreprise obtient un score positif sur ${score}/9 signaux fondamentaux couvrant la rentabilité, l'effet de levier et l'efficacité opérationnelle.`;
    } else if (score >= 4) {
      interpretation = `Qualité comptable mixte. ${score}/9 signaux sont positifs. Ni force ni faiblesse marquée.`;
    } else {
      interpretation = `Qualité comptable faible. Seulement ${score}/9 signaux sont positifs. La recherche historique suggère une sous-performance par rapport aux pairs dans des segments value similaires.`;
    }
    
    // Add coverage note if partial
    if (coverage && coverage !== '9/9' && coverage !== 'insufficient') {
      const [num, total] = coverage.split('/');
      const missing = 9 - parseInt(num);
      interpretation += ` ${missing} des 9 signaux n'ont pas pu être calculés en raison de données historiques manquantes. Le score reflète ${coverage} signaux.`;
    }
    
    return interpretation;
  };

  const getBeneishInterpretation = () => {
    if (isVietnam) {
      return "L'intégration des données financières du marché vietnamien est en cours. Les modèles de scoring seront disponibles dans une future mise à jour. Les sources actuelles ne fournissent pas une profondeur historique suffisante pour un calcul fiable.";
    }
    if (stock?.beneish_zone === 'not_applicable_financials') {
      return "Beneish a explicitement exclu les institutions financières de la calibration du modèle. L'appliquer aux banques ou assureurs produit des résultats peu fiables.";
    }
    if (stock?.beneish_coverage === 'insufficient' || stock?.beneish_mscore === null || stock?.beneish_mscore === undefined) {
      return "Données financières insuffisantes pour calculer ce score. La situation peut évoluer à mesure que de nouvelles données deviennent disponibles.";
    }
    
    const score = stock?.beneish_mscore;
    const coverage = stock?.beneish_coverage;
    let interpretation = '';
    
    if (score < -2.22) {
      interpretation = "Faible probabilité de manipulation des résultats. La combinaison des 8 variables comptables ne suggère pas de reporting agressif.";
    } else {
      interpretation = "Probabilité élevée de manipulation des résultats. Le modèle signale des schémas historiquement associés à une comptabilité agressive. Il s'agit d'un signal statistique, pas d'une accusation — investiguer avant de conclure.";
    }
    
    // Add coverage note if partial
    if (coverage && coverage !== '8/8' && coverage !== 'full' && coverage !== 'insufficient') {
      const [num] = coverage.split('/');
      const missing = 8 - parseInt(num);
      interpretation += ` ${missing} des 8 variables n'a pas pu être calculée. Interpréter le score avec prudence.`;
    }
    
    return interpretation;
  };

  // Modal content
  const modalContent = {
    altman: {
      title: "Altman Z''-Score",
      content: `Le Z''-Score d'Altman est un modèle de prédiction de faillite développé par Edward Altman (NYU) en 1993, spécifiquement calibré pour les marchés émergents et les entreprises non manufacturières.

Il combine 4 ratios financiers :
• Fonds de roulement / Total de l'actif
• Résultats mis en réserve / Total de l'actif
• EBIT / Total de l'actif
• Valeur comptable des capitaux propres / Total du passif

Zones :
• Safe : Z >= 2.6
• Grey : 1.1 <= Z < 2.6
• Distress : Z < 1.1

Précision : ~80% de classification correcte 2 ans avant la faillite (recherche originale sur échantillon marchés émergents).

Non applicable aux : banques, compagnies d'assurance, institutions financières diversifiées.

Source : Altman, E.I. (1993). Corporate Financial Distress and Bankruptcy (2e éd.). John Wiley & Sons.`
    },
    piotroski: {
      title: "Piotroski F-Score",
      content: `Le F-Score de Piotroski est un score de qualité comptable développé par Joseph Piotroski (Université de Chicago) en 2000, conçu pour identifier les entreprises financièrement solides dans les segments value.

Il agrège 9 signaux binaires selon trois dimensions :

Rentabilité (4 signaux) :
• ROA > 0
• Flux de trésorerie opérationnel > 0
• Variation ROA > 0
• Flux de trésorerie > Résultat net

Levier/Liquidité (3 signaux) :
• Variation dette long terme <= 0
• Variation ratio de liquidité > 0
• Pas d'émission d'actions nouvelles

Efficacité opérationnelle (2 signaux) :
• Variation marge brute > 0
• Variation rotation de l'actif > 0

Plage : 0-9 (9 = tous les signaux positifs)

Interprétation :
• 7-9 : Solide
• 4-6 : Neutre
• 0-3 : Faible

Résultats historiques : les portefeuilles value filtrés par F-Score >= 8 ont surperformé les portefeuilles value élargis de 7,5% par an (échantillon US 1976-1996).

Non applicable aux : institutions financières (les signaux F5 et F6 sont structurellement biaisés sur les bilans bancaires).

Source : Piotroski, J.D. (2000). Value Investing: The Use of Historical Financial Statement Information to Separate Winners from Losers. Journal of Accounting Research, 38, 1-41.`
    },
    beneish: {
      title: "Beneish M-Score",
      content: `Le M-Score de Beneish est un modèle de détection de manipulation des résultats développé par Messod Beneish (Université de l'Indiana) en 1999.

Il combine 8 variables dérivées des variations annuelles des états financiers :

• DSRI : Days Sales in Receivables Index (délai de recouvrement)
• GMI : Gross Margin Index (marge brute)
• AQI : Asset Quality Index (qualité de l'actif)
• SGI : Sales Growth Index (croissance des ventes)
• DEPI : Depreciation Index (amortissements)
• SGAI : Sales, General & Administrative Expenses Index
• TATA : Total Accruals to Total Assets (charges à payer)
• LVGI : Leverage Index (endettement)

Seuil : M < -2.22 = faible probabilité de manipulation

Résultats historiques : dans l'échantillon Beneish et al. (1999), le modèle a correctement signalé 76% des manipulateurs identifiés par la SEC.

À noter : le M-Score de Beneish avait signalé Enron comme manipulateur probable en 1998, deux ans avant son effondrement.

Important : le score est un schéma statistique, pas une preuve de fraude. Des entreprises peuvent déclencher des scores élevés en raison de changements comptables légitimes (restructurations, M&A, éléments exceptionnels).

Non applicable aux : institutions financières (Beneish les a explicitement exclues de la calibration du modèle).

Source : Beneish, M.D. (1999). The Detection of Earnings Manipulation. Financial Analysts Journal, 55(5), 24-36.`
    }
  };

  const renderScoreCard = (model, title, subtitle, score, zone, coverage, interpretation, icon) => {
    const isNA = zone === 'not_applicable_financials';
    const isInsufficient = coverage === 'insufficient' || (score === null || score === undefined);
    
    let formattedScore, scoreColor;
    if (model === 'altman') {
      formattedScore = formatAltman(score);
      scoreColor = getAltmanColor(zone, score);
    } else if (model === 'piotroski') {
      formattedScore = formatPiotroski(score);
      scoreColor = getPiotoskiColor(score);
    } else {
      formattedScore = formatBeneish(score);
      scoreColor = getBeneishColor(score);
    }

    // Coverage indicator text
    let coverageText = null;
    if (coverage && coverage !== 'insufficient') {
      if (model === 'piotroski' && coverage !== '9/9') {
        coverageText = `Calculé sur ${coverage} signaux`;
      } else if (model === 'beneish' && coverage !== '8/8') {
        coverageText = `Calculé sur ${coverage} variables`;
      }
    }

    return (
      <Card className="bg-[#0f0f23] border border-[#2a2a3e]">
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              {icon}
              <div>
                <h4 className="text-white font-medium">{title}</h4>
                <p className="text-gray-500 text-xs">{subtitle}</p>
              </div>
            </div>
            <Dialog open={openModal === model} onOpenChange={(open) => setOpenModal(open ? model : null)}>
              <DialogTrigger asChild>
                <button className="text-xs text-gray-500 hover:text-gray-300 underline underline-offset-2">
                  En savoir plus
                </button>
              </DialogTrigger>
              <DialogContent className="bg-[#1a1a2e] border-[#2a2a3e] text-white max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-xl">{modalContent[model].title}</DialogTitle>
                </DialogHeader>
                <div className="mt-4 text-sm text-gray-300 whitespace-pre-line leading-relaxed">
                  {modalContent[model].content}
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Score display */}
          <div className="mb-3">
            {isVietnam ? (
              <Badge 
                className="text-sm px-3 py-1"
                style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}
              >
                Données en attente
              </Badge>
            ) : isNA ? (
              <Badge 
                className="text-sm px-3 py-1"
                style={{ backgroundColor: '#E5E7EB', color: '#6B7280' }}
              >
                N/A — Financières
              </Badge>
            ) : isInsufficient ? (
              <span className="text-2xl text-gray-500">—</span>
            ) : (
              <div className="flex items-baseline gap-2">
                <span className={`text-2xl font-bold ${scoreColor}`}>
                  {formattedScore}
                </span>
                {zone && (() => {
                  // Zone label mapping per model — covers extended backend taxonomy
                  // Altman: safe / grey / distress
                  // Piotroski: strong / mixed / weak (+ legacy safe/grey/distress tolerated)
                  // Beneish: unlikely_manipulator / likely_manipulator (+ legacy safe/grey/distress tolerated)
                  const zoneConfig = {
                    // Altman canonical
                    safe: { label: 'Sûr', cls: 'bg-emerald-500/20 text-emerald-400' },
                    grey: { label: 'Zone grise', cls: 'bg-amber-500/20 text-amber-400' },
                    distress: { label: 'Détresse', cls: 'bg-red-500/20 text-red-400' },
                    // Piotroski canonical
                    strong: { label: 'Solide', cls: 'bg-emerald-500/20 text-emerald-400' },
                    mixed: { label: 'Mixte', cls: 'bg-amber-500/20 text-amber-400' },
                    weak: { label: 'Faible', cls: 'bg-red-500/20 text-red-400' },
                    // Beneish canonical
                    unlikely_manipulator: { label: 'Peu probable', cls: 'bg-emerald-500/20 text-emerald-400' },
                    likely_manipulator: { label: 'Probable', cls: 'bg-red-500/20 text-red-400' },
                  };
                  const cfg = zoneConfig[zone];
                  if (cfg) {
                    return <Badge className={`text-xs ${cfg.cls}`}>{cfg.label}</Badge>;
                  }
                  // Unknown zone value — render raw in italic as fallback
                  return (
                    <Badge className="text-xs bg-gray-500/20 text-gray-400 italic">
                      {zone}
                    </Badge>
                  );
                })()}
              </div>
            )}
            {coverageText && (
              <p className="text-xs text-gray-500 mt-1">{coverageText}</p>
            )}
          </div>

          {/* Interpretation */}
          {interpretation && (
            <p className="text-sm text-gray-400 leading-relaxed">
              {interpretation}
            </p>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Card className="lg:col-span-3 bg-[#1a1a2e] border border-[#2a2a3e] rounded-xl">
      <CardHeader className="border-b border-[#2a2a3e]">
        <CardTitle className="text-white flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-[#00ff88]" />
          Qualité Comptable & Santé Financière
        </CardTitle>
        <p className="text-gray-400 text-sm mt-1">
          Trois modèles académiques évaluent le risque de faillite, la qualité comptable et la probabilité de manipulation des résultats.
        </p>
      </CardHeader>
      <CardContent className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {renderScoreCard(
            'altman',
            "Altman Z''-Score",
            "Risque de faillite (marchés émergents)",
            stock?.altman_zscore,
            stock?.altman_zone,
            null,
            getAltmanInterpretation(),
            <TrendingUp className="w-5 h-5 text-blue-400" />
          )}
          {renderScoreCard(
            'piotroski',
            "Piotroski F-Score",
            "Qualité comptable (9 signaux)",
            stock?.piotroski_fscore,
            stock?.piotroski_zone,
            stock?.piotroski_coverage,
            getPiotoskiInterpretation(),
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
          )}
          {renderScoreCard(
            'beneish',
            "Beneish M-Score",
            "Probabilité de manipulation des résultats",
            stock?.beneish_mscore,
            stock?.beneish_zone,
            stock?.beneish_coverage,
            getBeneishInterpretation(),
            <AlertTriangle className="w-5 h-5 text-amber-400" />
          )}
        </div>

        {/* Methodology note */}
        <div className="p-3 bg-[#0f0f23] rounded-lg border border-[#2a2a3e]">
          <p className="text-xs text-gray-500 leading-relaxed">
            <span className="text-gray-400 font-medium">Note méthodologique :</span> les entreprises du secteur financier (banques, assurance, financières diversifiées) sont exclues des trois modèles de scoring, conformément aux spécifications originales (Altman 1993, Piotroski 2000, Beneish 1999). Lorsque certaines variables d'un score ne peuvent pas être calculées à partir des données sources, le score est marqué comme partiel avec un indicateur de couverture explicite.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ScoringSection;
